import React, { useState, useEffect } from 'react';
import notificationService from '../services/notificationService';
import '../i18n/config';
import { useI18n } from '../contexts/I18nContext';

const NotificationSettings: React.FC = () => {
  const { t } = useI18n();
  const [isSupported, setIsSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);

  useEffect(() => {
    checkSupport();
    checkPermission();
    checkSubscription();
  }, []);

  const checkSupport = () => {
    setIsSupported(notificationService.isSupported());
  };

  const checkPermission = async () => {
    const status = await notificationService.getPermissionStatus();
    setPermissionStatus(status);
  };

  const checkSubscription = async () => {
    try {
      const sub = await notificationService.getSubscription();
      setSubscription(sub);
      setIsSubscribed(!!sub);
    } catch (error) {
      console.error('Erro ao verificar subscription:', error);
    }
  };

  const handleRequestPermission = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const permission = await notificationService.requestPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        await handleSubscribe();
      } else if (permission === 'denied') {
        setError(t('notifications.settings.permissionDenied'));
      }
    } catch (error: any) {
      setError(error.message || t('notifications.settings.error.requestPermission'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const sub = await notificationService.subscribeToPush();
      await notificationService.registerSubscription(sub);
      setSubscription(sub);
      setIsSubscribed(true);
    } catch (error: any) {
      setError(error.message || t('notifications.settings.error.activate'));
      setIsSubscribed(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await notificationService.unsubscribeFromPush();
      setSubscription(null);
      setIsSubscribed(false);
    } catch (error: any) {
      setError(error.message || t('notifications.settings.error.deactivate'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await notificationService.sendTestNotification();
    } catch (error: any) {
      setError(error.message || t('notifications.settings.error.sendTest'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#fee', borderRadius: '8px', margin: '20px 0' }}>
        <h3>{t('notifications.settings.notSupported')}</h3>
        <p>{t('notifications.settings.notSupportedDesc')}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '20px' }}>{t('notifications.settings.title')}</h2>

      {error && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          borderRadius: '8px', 
          marginBottom: '20px',
          color: '#c00'
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <strong>{t('notifications.settings.permissionStatus')}</strong>{' '}
        <span style={{
          color: permissionStatus === 'granted' ? 'green' : 
                 permissionStatus === 'denied' ? 'red' : 'orange'
        }}>
          {permissionStatus === 'granted' ? t('notifications.settings.permission.granted') :
           permissionStatus === 'denied' ? t('notifications.settings.permission.denied') : t('notifications.settings.permission.default')}
        </span>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <strong>{t('notifications.settings.subscriptionStatus')}</strong>{' '}
        <span style={{ color: isSubscribed ? 'green' : 'gray' }}>
          {isSubscribed ? t('notifications.settings.subscription.active') : t('notifications.settings.subscription.inactive')}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {permissionStatus !== 'granted' && (
          <button
            onClick={handleRequestPermission}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isLoading ? t('notifications.settings.loading') : t('notifications.settings.activate')}
          </button>
        )}

        {permissionStatus === 'granted' && !isSubscribed && (
          <button
            onClick={handleSubscribe}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isLoading ? t('notifications.settings.connecting') : t('notifications.settings.activatePush')}
          </button>
        )}

        {isSubscribed && (
          <>
            <button
              onClick={handleTestNotification}
              disabled={isLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isLoading ? t('notifications.settings.sending') : t('notifications.settings.sendTest')}
          </button>

            <button
              onClick={handleUnsubscribe}
              disabled={isLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isLoading ? t('notifications.settings.deactivating') : t('notifications.settings.deactivate')}
          </button>
          </>
        )}
      </div>

      {subscription && (
        <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '8px', fontSize: '12px' }}>
          <strong>Endpoint:</strong> {subscription.endpoint.substring(0, 50)}...
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;

